/**
 * A class for handling header events like opening menus and managing focus.
 *
 * @class
 */
class HeaderEvents {

	/**
	 * Initializes class
	 *
	 * @class
	 */
	constructor() {

		this.header_buttons = document.querySelectorAll( '.js-toggle-button' );
		this.menu_buttons_main = document.querySelectorAll( '.js-menu-header-trigger.c-menu-header__li-link--0' );
		this.menu_buttons_sub = document.querySelectorAll( '.js-menu-header-trigger:not(.c-menu-header__li-link--0)' );

		this.header = document.querySelector( '.page-header' );
		this.menu = document.getElementById( 'menu-primary-menu' );

		this.openComponents = [];
		this.scrollPosition = 0;

		// Bind event listeners to header buttons
		this.initHeaderEvents();
		this.addHeaderLeaveEvent();

	}

	/**
	 * Initializes event listeners for header buttons
	 */
	initHeaderEvents() {

		this.header_buttons.forEach( button => {

			button.addEventListener( 'click', ( event ) => this.toggleComponent( event, button ) );
			button.addEventListener( 'keydown', ( event ) => {

				if ( event.key === 'Enter' || event.keyCode === 13 ) {

					this.toggleComponent( event, button );

				}

			} );

		} );

		this.menu_buttons_main.forEach( button => {

			button.addEventListener( 'click', ( event ) => this.toggleMenuFirstLevel( event, button ) );
			button.addEventListener( 'keydown', ( event ) => {

				if ( event.key === 'Enter' || event.keyCode === 13 ) {

					this.toggleMenuFirstLevel( event, button );

				}

			} );

			button.addEventListener( 'mouseenter', ( event ) => {

				const mediaQuery = window.matchMedia( '(min-width: 1201px)' );
				if ( mediaQuery.matches && button.ariaExpanded !== "true" ) {

					this.toggleComponent( event, button );

				}

			} );

		} );

		this.menu_buttons_sub.forEach( button => {

			button.addEventListener( 'click', ( event ) => this.toggleSubmenu( event, button ) );
			button.addEventListener( 'keydown', ( event ) => {

				if ( event.key === 'Enter' || event.keyCode === 13 ) {

					this.toggleSubmenu( event, button );

				}

			} );

		} );

	}

	/**
	 * Adds event to detect when mouse leaves the page header area
	 */
	addHeaderLeaveEvent() {

		this.header.addEventListener( 'mouseleave', ( event ) => {

			const mediaQuery = window.matchMedia( '(min-width: 1201px)' );

			if ( mediaQuery.matches ) {

				const relatedTarget = event.relatedTarget;
				if ( !this.header.contains( relatedTarget ) ) {

					this.closeAllComponents();

				}

			}

		} );

	}

	/**
	 * Opens the component controlled by the button
	 * @param {HTMLElement} button
	 * @param {Boolean} bodyClass
	 */
	openComponent( button, bodyClass = true ) {

		const controls = button.getAttribute( 'aria-controls' );
		const component = document.getElementById( controls );

		if ( component ) {

			component.classList.add( 'is-open' );
			setTimeout( () => {

				component.classList.add( 'is-open--fade-in' );

			}, 10 );

			component.removeAttribute( 'hidden' );
			button.setAttribute( 'aria-expanded', 'true' );

			if ( bodyClass ) {

				document.body.classList.add( 'header-open-component' );

			}

			// Get focusable elements within the component
			const focusableElements = this.getFocusableElements( component );
			// Add the toggle button at the start of the array
			focusableElements.unshift( button );

			// Add component information to the stack
			this.openComponents.push( {
				component: component,
				focusableElements: focusableElements,
				focusIndex: 0
			} );

			// Update current focusable elements and index
			this.focusableElements = focusableElements;
			this.focusIndex = 0;

			// Add keydown event listener if this is the first component
			if ( this.openComponents.length === 1 ) {

				this.handleKeyDownBound = this.handleKeyDown.bind( this );
				document.addEventListener( 'keydown', this.handleKeyDownBound );

			}

		}

	}

	/**
	 * Closes the component controlled by the button
	 * @param {HTMLElement} button
	 * @param {Boolean} bodyClass
	 */
	closeComponent( button, bodyClass = true ) {

		const controls = button.getAttribute( 'aria-controls' );
		const component = document.getElementById( controls );

		if ( component ) {

			// Close all nested components
			const nestedButtons = component.querySelectorAll( '[aria-expanded="true"]' );
			nestedButtons.forEach( nestedButton => {

				this.closeComponent( nestedButton, bodyClass );

			} );

			// Remove the component from the stack
			this.openComponents = this.openComponents.filter( openComp => openComp.component !== component );

			component.setAttribute( 'hidden', '' );
			button.setAttribute( 'aria-expanded', 'false' );
			component.classList.remove( 'is-open' );
			component.classList.remove( 'is-open--fade-in' );

			// Update current focusable elements
			if ( this.openComponents.length > 0 ) {

				const currentComponent = this.openComponents[this.openComponents.length - 1];
				this.focusableElements = currentComponent.focusableElements;
				this.focusIndex = currentComponent.focusIndex;

			} else {

				// No open components, remove the keydown listener.
				if ( this.handleKeyDownBound ) {

					document.removeEventListener( 'keydown', this.handleKeyDownBound );
					this.handleKeyDownBound = null;

				}
				this.focusableElements = null;
				this.focusIndex = null;

				if ( bodyClass ) {

					document.body.classList.remove( 'header-open-component' );

					// Restore the scroll position.
					const mediaQuery = window.matchMedia( '(min-width: 1201px)' );

					if ( !mediaQuery.matches ) {

						window.scrollTo( 0, this.scrollPosition );

					}

				}

			}

		}

	}

	/**
	 * Toggles the component based on the button's aria-expanded state
	 * @param {Event} event
	 * @param {HTMLElement} button
	 */
	toggleComponent( event, button ) {

		if ( event.type === 'mouseenter' || event.type === 'click' || ( event.type === 'keyup' && ( event.key === 'Enter' || event.keyCode === 13 ) ) ) {

			const isExpanded = button.getAttribute( 'aria-expanded' ) === 'true';

			if ( isExpanded ) {

				this.closeComponent( button );

			} else {

				// Save the scroll position before opening the menu.
				this.scrollPosition = window.scrollY;

				// First close all other components
				this.closeAllComponents();
				this.openComponent( button );

			}

		}

	}

	/**
	 * Closes all open components and deactivates them
	 */
	closeAllComponents() {

		this.header_buttons.forEach( button => {

			this.closeComponent( button );

		} );

	}

	/**
	 * Toggles the first-level menu based on the current viewport and the button's aria-expanded state.
	 *
	 * @param {Event} event - The event that triggered the action.
	 * @param {HTMLElement} button - The button that controls the menu.
	 */
	toggleMenuFirstLevel( event, button ) {

		const mediaQuery = window.matchMedia( '(min-width: 1201px)' );

		if ( mediaQuery.matches ) {

			this.toggleComponent( event, button );

		} else if ( event.type === 'click' || ( event.type === 'keyup' && ( event.key === 'Enter' || event.keyCode === 13 ) ) ) {

			const isExpanded = button.getAttribute( 'aria-expanded' ) === 'true';

			if ( isExpanded ) {

				this.closeComponent( button, false );

			} else {

				this.closeAllMenuItems();
				this.openComponent( button );

			}

		}

	}

	/**
	 * Closes all open menu items at the first level.
	 */
	closeAllMenuItems() {

		this.menu_buttons_main.forEach( button => {

			this.closeComponent( button );

		} );

	}

	/**
	 * Toggles the submenu open or closed based on the button's aria-expanded state.
	 *
	 * @param {Event} event - The event that triggered the action.
	 * @param {HTMLElement} button - The button that controls the submenu.
	 */
	toggleSubmenu( event, button ) {

		if ( event.type === 'click' || ( event.type === 'keyup' && ( event.key === 'Enter' || event.keyCode === 13 ) ) ) {

			const isExpanded = button.getAttribute( 'aria-expanded' ) === 'true';

			if ( isExpanded ) {

				this.closeComponent( button, false );

			} else {

				this.openSubmenu( button );

			}

		}

	}

	/**
	 * Closes all open submenus and deactivates them
	 */
	closeAllSubmenus() {

		this.menu_buttons_sub.forEach( button => {

			this.closeComponent( button, false );

		} );

	}

	/**
	 * Opens the specified submenu and closes all other submenus.
	 *
	 * @param {HTMLElement} button - The button that controls the submenu.
	 */
	openSubmenu( button ) {

		// First close all other submenus
		this.closeAllSubmenus();
		// Open the selected submenu
		this.openComponent( button, false ); // Do not add body class

	}

	/**
	 * Handles keydown events for trapping focus within a component
	 * @param {Event} e
	 */
	handleKeyDown( e ) {

		// Check if we have active focusable elements
		if ( !this.focusableElements || this.focusableElements.length === 0 ) {

			return;

		}

		if ( e.key === 'Tab' || e.keyCode === 9 ) {

			e.preventDefault();

			const focusableElements = this.focusableElements;
			let focusIndex = this.focusIndex;

			if ( e.shiftKey ) {

				// Shift + Tab
				focusIndex--;
				if ( focusIndex < 0 ) {

					focusIndex = focusableElements.length - 1; // Move back to the last element

				}

			} else {

				// Tab
				focusIndex++;
				if ( focusIndex >= focusableElements.length ) {

					focusIndex = 0; // Move to the first element

				}

			}

			// Set focus on the new element
			this.focusIndex = focusIndex;
			focusableElements[focusIndex].focus();

		} else if ( e.key === 'Escape' || e.keyCode === 27 ) {

			// Close the current component on Escape key press
			const currentComponent = this.openComponents[this.openComponents.length - 1];
			if ( currentComponent ) {

				const button = currentComponent.focusableElements[0];
				this.closeComponent( button );
				button.focus();

			}

		}

	}


	/**
	 * Retrieves all focusable and visible elements within a component
	 * @param {HTMLElement} component
	 * @return {Array}
	 */
	getFocusableElements( component ) {

		const focusableSelectors = [
			'a[href]:not([tabindex^="-"]):not([hidden]):not([aria-hidden="true"])',
			'button:not([disabled]):not([tabindex^="-"]):not([hidden]):not([aria-hidden="true"])',
			'[tabindex]:not([tabindex^="-"]):not([hidden]):not([aria-hidden="true"])'
		];

		let elements = component.querySelectorAll( focusableSelectors.join( ',' ) );
		elements = Array.prototype.slice.call( elements );

		// Filter only visible elements
		elements = elements.filter( ( element ) => {

			return this.isVisible( element );

		} );

		return elements;

	}

	/**
	 * Checks if an element is visible
	 * @param {HTMLElement} element
	 * @return {Boolean}
	 */
	isVisible( element ) {

		const style = window.getComputedStyle( element );
		return style.display !== 'none' &&
			style.visibility !== 'hidden' &&
			element.offsetParent !== null;

	}

}

export default HeaderEvents;
